import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useDeviceSelectors } from 'react-device-detect';
import scribeTrack from 'scribe/scribe-track';
import { TRACKING_PAGE_TYPE, TRACKING_SOURCE } from 'scribe/constant';
import { GRAB_APP_LINK } from 'constants/app-constants';
import { inGrabApp } from 'common/utils/platform';
import { appDataSelector } from 'selectors/app-data-selector';
import instrumentationDuck from 'ducks/instrumentation-duck';
import { ViewPortProvider } from '../context/view-port';
import InstallationBanner from '../components/banner/installation-banner';
import BookingTracker from '../components/booking-tracker/booking-tracker';
import useUserType from '../hooks/useUserType';
import MapDuck from '../ducks/map-duck';
import DriverDuck from '../ducks/driver-duck';
import TripDuck from '../ducks/trip-duck';
import styles from './track.less';

const TrackPage = ({ isError, isExpired, guid }) => {
  const dispatch = useDispatch();
  const [deviceSelector] = useDeviceSelectors();
  const userType = useUserType();
  const navigate = useNavigate();

  const {
    enableInstruction,
    enableWebTrackingUICashlessPOD,
    enableWebTrackingStayContactable,
  } = useSelector(appDataSelector);
  const MapData = useSelector(MapDuck.selectors.map);
  const DriverData = useSelector(DriverDuck.selectors.driver);
  const TripData = useSelector(TripDuck.selectors.trip);

  const inApp = useMemo(() => inGrabApp(), []);

  useEffect(
    () => {
      dispatch(
        instrumentationDuck.creators.updateTrackingInfo({
          trackingSource: inGrabApp()
            ? TRACKING_SOURCE.GRAB_INBOX
            : TRACKING_SOURCE.TRACKING_LINK,
          trackingPageType: TRACKING_PAGE_TYPE.NEW_TRACKING_LINK,
          orderId: TripData.orderId,
        }),
      );
    },
    [TripData.orderId],
  );

  useEffect(() => {
    scribeTrack.expressTracking.default({
      trackingSource: inGrabApp()
        ? TRACKING_SOURCE.GRAB_INBOX
        : TRACKING_SOURCE.TRACKING_LINK,
      isTrackingLinkExpired: isExpired,
      orderIdDisplayed: TripData.orderId,
      isInApp: inGrabApp(),
      trackingPageType: TRACKING_PAGE_TYPE.NEW_TRACKING_LINK,
    });
  }, []);

  useEffect(
    () => {
      if (isError) {
        navigate('/error');
      } else if (isExpired) {
        navigate('/expired');
      }
    },
    [isError, isExpired, navigate],
  );
  return (
    <div className={styles.page}>
      {!inApp && <InstallationBanner link={GRAB_APP_LINK} />}
      <ViewPortProvider>
        <BookingTracker
          map={MapData}
          driver={DriverData}
          trip={TripData}
          userType={userType}
          guid={guid}
          featureFlag={{
            hideCallAndChat: !inApp && deviceSelector.isBrowser,
            hideAddInstruction: !enableInstruction,
            hideCashlessPOD: !enableWebTrackingUICashlessPOD,
            hideStayContactable: !enableWebTrackingStayContactable,
          }}
        />
      </ViewPortProvider>
    </div>
  );
};

TrackPage.propTypes = {
  // TODO:: Remove after release - @wesley.loh
  isError: PropTypes.bool.isRequired,
  isExpired: PropTypes.bool.isRequired,
  guid: PropTypes.string.isRequired,
};

export default TrackPage;
